import moment from "moment";

export const round2 = (value) => {
  const num = typeof value === "string" ? parseFloat(value) : (value || 0);
  if (isNaN(num)) return 0;
  const sign = num < 0 ? -1 : 1;
  return sign * (Math.round((Math.abs(num) + Number.EPSILON) * 100) / 100);
};

export const FormatCurrencyPlain = (value) => {
  const num = typeof value === "string" ? parseFloat(value) : (value || 0);
  if (isNaN(num)) return "$0.00";
  const rounded = round2(num);
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rounded);
};

export const CurrencyFormatter = (value) => {
  const num = typeof value === "string" ? parseFloat(value) : (value || 0);
  if (isNaN(num)) return "$0.00";
  const rounded = round2(num);
  const formatted = FormatCurrencyPlain(Math.abs(rounded));
  if (rounded < 0 || Object.is(rounded, -0)) {
    return `(${formatted})`;
  }
  return formatted;
};

/**
 * Generates all month-end periods from January 2025 up to the current date (or end of current year).
 */
export const generateMonthsList = (startYear = 2025, startMonth = 1) => {
  const list = [];
  const start = moment(`${startYear}-${String(startMonth).padStart(2, "0")}-01`, "YYYY-MM-DD");
  const now = moment().endOf("month");
  
  // Also include the remainder of the current year so future months can be viewed/selected
  const endOfCurrentYear = moment().endOf("year");
  const endLimit = now.isAfter(endOfCurrentYear) ? now : endOfCurrentYear;

  let current = start.clone();
  while (current.isSameOrBefore(endLimit, "month")) {
    const monthEnd = current.clone().endOf("month");
    const monthStart = current.clone().startOf("month");
    const dueDate = monthEnd.clone().add(30, "days"); // Section 13 requirement: within 30 days of month-end

    list.push({
      id: current.format("YYYY-MM"),
      year: current.year(),
      monthIndex: current.month(), // 0 to 11
      monthNumber: current.month() + 1, // 1 to 12
      yearMonth: current.format("YYYY-MM"),
      label: current.format("MMMM YYYY"),
      shortLabel: current.format("MMM YYYY"),
      monthStart: monthStart.toISOString(),
      monthEnd: monthEnd.toISOString(),
      lastDayFormatted: monthEnd.format("LL"),
      monthEndDateOnly: monthEnd.format("YYYY-MM-DD"),
      dueDateFormatted: dueDate.format("LL"),
      isPastOrCurrent: current.isSameOrBefore(moment(), "month"),
    });

    current.add(1, "month");
  }

  return list;
};

/**
 * Calculates full Section 13 O. Reg. 579/05 trust reconciliation for a given month for BOTH trust accounts.
 */
export const calculateMonthReconciliation = (monthObj, transactions = [], deals = []) => {
  const dealsList = deals || [];

  // Normalize all incoming transactions to guarantee rounded values
  const normalizedTxns = (transactions || []).map((t) => ({
    ...t,
    amount: round2(t.amount),
  }));

  const mStart = moment(monthObj.monthStart);
  const mEnd = moment(monthObj.monthEnd);

  // 1. Transactions prior to this month
  const rePriorTxns = normalizedTxns.filter(
    (t) =>
      t.account === "real_estate_trust" &&
      t.date &&
      moment(t.date).isBefore(mStart, "day")
  );
  const commPriorTxns = normalizedTxns.filter(
    (t) =>
      t.account === "commission_trust" &&
      t.date &&
      moment(t.date).isBefore(mStart, "day")
  );

  const reOpeningBalance = round2(
    rePriorTxns
      .filter((t) => t.type === "debit")
      .reduce((acc, t) => acc + t.amount, 0) -
    rePriorTxns
      .filter((t) => t.type === "credit")
      .reduce((acc, t) => acc + t.amount, 0)
  );

  const commOpeningBalance = round2(
    commPriorTxns
      .filter((t) => t.type === "debit")
      .reduce((acc, t) => acc + t.amount, 0) -
    commPriorTxns
      .filter((t) => t.type === "credit")
      .reduce((acc, t) => acc + t.amount, 0)
  );

  // 2. Real Estate Trust Account Activity in Month
  const reMonthTxns = normalizedTxns.filter(
    (t) =>
      t.account === "real_estate_trust" &&
      t.date &&
      moment(t.date).isSameOrAfter(mStart, "day") &&
      moment(t.date).isSameOrBefore(mEnd, "day")
  ).sort((a, b) => new Date(a.date) - new Date(b.date));

  const reTotalDebits = round2(
    reMonthTxns
      .filter((t) => t.type === "debit")
      .reduce((acc, t) => acc + t.amount, 0)
  );
  const reTotalCredits = round2(
    reMonthTxns
      .filter((t) => t.type === "credit")
      .reduce((acc, t) => acc + t.amount, 0)
  );
  const reClosingBalance = round2(reOpeningBalance + reTotalDebits - reTotalCredits);
  const reNetMonthlyActivity = round2(reTotalDebits - reTotalCredits);

  // 3. Commission Trust Account Activity in Month
  const commMonthTxns = normalizedTxns.filter(
    (t) =>
      t.account === "commission_trust" &&
      t.date &&
      moment(t.date).isSameOrAfter(mStart, "day") &&
      moment(t.date).isSameOrBefore(mEnd, "day")
  ).sort((a, b) => new Date(a.date) - new Date(b.date));

  const commTotalDebits = round2(
    commMonthTxns
      .filter((t) => t.type === "debit")
      .reduce((acc, t) => acc + t.amount, 0)
  );
  const commTotalCredits = round2(
    commMonthTxns
      .filter((t) => t.type === "credit")
      .reduce((acc, t) => acc + t.amount, 0)
  );
  const commClosingBalance = round2(commOpeningBalance + commTotalDebits - commTotalCredits);
  const commNetMonthlyActivity = round2(commTotalDebits - commTotalCredits);

  // 4. Combined Monthly Journal
  const allMonthTrustTxns = [...reMonthTxns, ...commMonthTxns].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  // Combined Summary
  const combinedOpeningBalance = round2(reOpeningBalance + commOpeningBalance);
  const combinedTotalDebits = round2(reTotalDebits + commTotalDebits);
  const combinedTotalCredits = round2(reTotalCredits + commTotalCredits);
  const combinedClosingBalance = round2(reClosingBalance + commClosingBalance);
  const combinedNetMonthlyActivity = round2(combinedTotalDebits - combinedTotalCredits);

  // 5. Client Trust Liability Schedules for Each Account and Combined (As of Month-End)
  const allTrustTxnsToDate = normalizedTxns.filter(
    (t) =>
      (t.account === "real_estate_trust" || t.account === "commission_trust") &&
      t.date &&
      moment(t.date).isSameOrBefore(mEnd, "day")
  );

  const dealsMap = {};
  dealsList.forEach((deal) => {
    if (deal.deal_id || deal._id) {
      const key = String(deal.deal_id || deal._id);
      dealsMap[key] = deal;
    }
  });

  const dealBalances = {};
  allTrustTxnsToDate.forEach((t) => {
    const dealKey = String(
      t.deal_no || t.deal_id || t.dealId || t.deal || "General / Unassigned"
    );
    if (!dealBalances[dealKey]) {
      const matchedDeal =
        dealsMap[dealKey] ||
        dealsList.find(
          (d) =>
            String(d.deal_id) === dealKey ||
            String(d._id) === dealKey ||
            (t.reference && String(d.deal_id) && t.reference.includes(String(d.deal_id)))
        );

      let clientNames = "";
      if (matchedDeal) {
        if (matchedDeal.buyers && matchedDeal.buyers.length > 0) {
          clientNames = matchedDeal.buyers.map((b) => b.name).filter(Boolean).join(", ");
        }
        if (!clientNames && matchedDeal.sellers && matchedDeal.sellers.length > 0) {
          clientNames = matchedDeal.sellers.map((s) => s.name).filter(Boolean).join(", ");
        }
      }

      dealBalances[dealKey] = {
        dealKey,
        dealNo: matchedDeal?.deal_id || (dealKey !== "General / Unassigned" ? dealKey : "-"),
        address: matchedDeal?.address || t.reference || "Dwelly Trust Funds",
        clientName: clientNames || t.details || "Buyer / Client",
        reTrustBalance: 0,
        commTrustBalance: 0,
        totalBalance: 0,
        hasReActivityThisMonth: false,
        hasCommActivityThisMonth: false,
        hasActivityThisMonth: false,
        lastActivityDate: t.date,
      };
    }

    const amt = t.type === "debit" ? t.amount : -t.amount;
    const isThisMonth =
      moment(t.date).isSameOrAfter(mStart, "day") &&
      moment(t.date).isSameOrBefore(mEnd, "day");

    if (t.account === "real_estate_trust") {
      dealBalances[dealKey].reTrustBalance = round2(dealBalances[dealKey].reTrustBalance + amt);
      if (isThisMonth) dealBalances[dealKey].hasReActivityThisMonth = true;
    } else if (t.account === "commission_trust") {
      dealBalances[dealKey].commTrustBalance = round2(dealBalances[dealKey].commTrustBalance + amt);
      if (isThisMonth) dealBalances[dealKey].hasCommActivityThisMonth = true;
    }

    dealBalances[dealKey].totalBalance = round2(
      dealBalances[dealKey].reTrustBalance + dealBalances[dealKey].commTrustBalance
    );

    if (isThisMonth) {
      dealBalances[dealKey].hasActivityThisMonth = true;
    }

    if (
      !dealBalances[dealKey].lastActivityDate ||
      new Date(t.date) > new Date(dealBalances[dealKey].lastActivityDate)
    ) {
      dealBalances[dealKey].lastActivityDate = t.date;
    }
  });

  // Normalize 1¢ rounding artifacts (<= $0.01) to 0 across deal balances
  Object.values(dealBalances).forEach((d) => {
    if (Math.abs(d.reTrustBalance) <= 0.015) {
      d.reTrustBalance = 0;
    }
    if (Math.abs(d.commTrustBalance) <= 0.015) {
      d.commTrustBalance = 0;
    }
    d.totalBalance = round2(d.reTrustBalance + d.commTrustBalance);
  });

  // Liability schedules (only active deals with genuine open trust liabilities > 1¢ held at month-end)
  const reTrustLiabilityList = Object.values(dealBalances).filter(
    (d) => Math.abs(d.reTrustBalance) > 0.015
  );
  let reTotalTrustLiability = round2(
    reTrustLiabilityList.reduce((acc, d) => acc + d.reTrustBalance, 0)
  );
  if (Math.abs(reClosingBalance - reTotalTrustLiability) <= 0.015) {
    reTotalTrustLiability = reClosingBalance;
  }

  const commTrustLiabilityList = Object.values(dealBalances).filter(
    (d) => Math.abs(d.commTrustBalance) > 0.015
  );
  let commTotalTrustLiability = round2(
    commTrustLiabilityList.reduce((acc, d) => acc + d.commTrustBalance, 0)
  );
  if (Math.abs(commClosingBalance - commTotalTrustLiability) <= 0.015) {
    commTotalTrustLiability = commClosingBalance;
  }

  const combinedTrustLiabilityList = Object.values(dealBalances).filter(
    (d) => Math.abs(d.totalBalance) > 0.015
  );
  let combinedTotalTrustLiability = round2(
    combinedTrustLiabilityList.reduce((acc, d) => acc + d.totalBalance, 0)
  );
  if (Math.abs(combinedClosingBalance - combinedTotalTrustLiability) <= 0.015) {
    combinedTotalTrustLiability = combinedClosingBalance;
  }

  // 6. Three-Way Reconciliations for Each Account
  // Real Estate Trust 3-Way
  const reBankStatementBalance = reClosingBalance;
  const reOutstandingDeposits = 0;
  const reOutstandingDisbursements = 0;
  const reReconciledBankBalance = round2(reBankStatementBalance + reOutstandingDeposits - reOutstandingDisbursements);
  const reVariance = Math.abs(reReconciledBankBalance - reClosingBalance) <= 0.015 ? 0 : round2(reReconciledBankBalance - reClosingBalance);
  const reLiabilityVariance = Math.abs(reClosingBalance - reTotalTrustLiability) <= 0.015 ? 0 : round2(reClosingBalance - reTotalTrustLiability);

  // Commission Trust 3-Way
  const commBankStatementBalance = commClosingBalance;
  const commOutstandingDeposits = 0;
  const commOutstandingDisbursements = 0;
  const commReconciledBankBalance = round2(commBankStatementBalance + commOutstandingDeposits - commOutstandingDisbursements);
  const commVariance = Math.abs(commReconciledBankBalance - commClosingBalance) <= 0.015 ? 0 : round2(commReconciledBankBalance - commClosingBalance);
  const commLiabilityVariance = Math.abs(commClosingBalance - commTotalTrustLiability) <= 0.015 ? 0 : round2(commClosingBalance - commTotalTrustLiability);

  // Combined 3-Way
  const combinedBankStatementBalance = combinedClosingBalance;
  const combinedVariance = Math.abs(combinedBankStatementBalance - combinedClosingBalance) <= 0.015 ? 0 : round2(combinedBankStatementBalance - combinedClosingBalance);
  const combinedLiabilityVariance = Math.abs(combinedClosingBalance - combinedTotalTrustLiability) <= 0.015 ? 0 : round2(combinedClosingBalance - combinedTotalTrustLiability);

  return {
    month: monthObj,
    periodLabel: monthObj.label,
    periodEndFormatted: monthObj.lastDayFormatted,
    dueDateFormatted: monthObj.dueDateFormatted,

    // Real Estate Trust Account
    reTrust: {
      accountName: "Real Estate Trust Account",
      accountType: "real_estate_trust",
      statutorySection: "O. Reg. 579/05, Section 13 (Real Estate Trust)",
      openingBalance: reOpeningBalance,
      totalDebits: reTotalDebits,
      totalCredits: reTotalCredits,
      netMonthlyActivity: reNetMonthlyActivity,
      closingBalance: reClosingBalance,
      txnsCount: reMonthTxns.length,
      monthlyJournal: reMonthTxns,
      trustLiabilityList: reTrustLiabilityList,
      totalTrustLiability: reTotalTrustLiability,
      threeWayReconciliation: {
        bankStatementBalance: reBankStatementBalance,
        outstandingDeposits: reOutstandingDeposits,
        outstandingDisbursements: reOutstandingDisbursements,
        reconciledBankBalance: reReconciledBankBalance,
        bookBalance: reClosingBalance,
        totalTrustLiability: reTotalTrustLiability,
        variance: reVariance,
        liabilityVariance: reLiabilityVariance,
        isBalanced: reVariance === 0 && reLiabilityVariance === 0,
        reconciliationStatus: reVariance === 0 && reLiabilityVariance === 0 ? "In Balance / Compliant" : "Out of Balance",
      },
    },

    // Commission Trust Account
    commTrust: {
      accountName: "Commission Trust Account",
      accountType: "commission_trust",
      statutorySection: "O. Reg. 579/05, Section 13 (Commission Trust)",
      openingBalance: commOpeningBalance,
      totalDebits: commTotalDebits,
      totalCredits: commTotalCredits,
      netMonthlyActivity: commNetMonthlyActivity,
      closingBalance: commClosingBalance,
      txnsCount: commMonthTxns.length,
      monthlyJournal: commMonthTxns,
      trustLiabilityList: commTrustLiabilityList,
      totalTrustLiability: commTotalTrustLiability,
      threeWayReconciliation: {
        bankStatementBalance: commBankStatementBalance,
        outstandingDeposits: commOutstandingDeposits,
        outstandingDisbursements: commOutstandingDisbursements,
        reconciledBankBalance: commReconciledBankBalance,
        bookBalance: commClosingBalance,
        totalTrustLiability: commTotalTrustLiability,
        variance: commVariance,
        liabilityVariance: commLiabilityVariance,
        isBalanced: commVariance === 0 && commLiabilityVariance === 0,
        reconciliationStatus: commVariance === 0 && commLiabilityVariance === 0 ? "In Balance / Compliant" : "Out of Balance",
      },
    },

    // Combined Trust Accounts Summary
    combined: {
      accountName: "All Trust Accounts (Real Estate Trust & Commission Trust)",
      openingBalance: combinedOpeningBalance,
      totalDebits: combinedTotalDebits,
      totalCredits: combinedTotalCredits,
      netMonthlyActivity: combinedNetMonthlyActivity,
      closingBalance: combinedClosingBalance,
      txnsCount: allMonthTrustTxns.length,
      monthlyJournal: allMonthTrustTxns,
      trustLiabilityList: combinedTrustLiabilityList,
      totalTrustLiability: combinedTotalTrustLiability,
      threeWayReconciliation: {
        bankStatementBalance: combinedBankStatementBalance,
        outstandingDeposits: 0,
        outstandingDisbursements: 0,
        reconciledBankBalance: combinedBankStatementBalance,
        bookBalance: combinedClosingBalance,
        totalTrustLiability: combinedTotalTrustLiability,
        variance: combinedVariance,
        liabilityVariance: combinedLiabilityVariance,
        isBalanced: combinedVariance === 0 && combinedLiabilityVariance === 0,
        reconciliationStatus: combinedVariance === 0 && combinedLiabilityVariance === 0 ? "In Balance / Compliant" : "Out of Balance",
      },
    },

    trustLiabilityList: combinedTrustLiabilityList,
    monthlyJournal: allMonthTrustTxns,
    threeWayReconciliation: {
      bankStatementBalance: combinedBankStatementBalance,
      outstandingDeposits: 0,
      outstandingDisbursements: 0,
      reconciledBankBalance: combinedBankStatementBalance,
      bookBalance: combinedClosingBalance,
      totalTrustLiability: combinedTotalTrustLiability,
      variance: combinedVariance,
      liabilityVariance: combinedLiabilityVariance,
      isBalanced: combinedVariance === 0 && combinedLiabilityVariance === 0,
      reconciliationStatus: combinedVariance === 0 && combinedLiabilityVariance === 0 ? "In Balance / Compliant" : "Out of Balance",
    },
  };
};
